import { useState, useEffect } from 'react';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { PageHeader } from '@/components/layout/PageContainer';
import { Filter, SlidersHorizontal, Archive, Palette } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { FilterDrawer } from './FilterDrawer';

interface ContentTrackerHeaderProps {
  totalCampaigns: number;
  filteredCount: number;
  isFiltered: boolean;
  filters: {
    channel: string;
    platform: string;
    quarter: string;
    market: string;
    business_unit: string;
    nameplate: string;
    format: string;
    plan_id: string;
  };
  onFilterChange: (filters: ContentTrackerHeaderProps['filters']) => void;
  availableOptions: Record<string, string[]>;
  showArchived: boolean;
  onShowArchivedChange: (value: boolean) => void;
  showColors: boolean;
  onShowColorsChange: (value: boolean) => void;
  activeFilterCount: number;
  onClearFilters: () => void;
  onTeamChange: () => void;
  onOpenColumnSettings: () => void;
}

export function ContentTrackerHeader({
  totalCampaigns,
  filteredCount,
  isFiltered,
  filters,
  onFilterChange,
  availableOptions,
  showArchived,
  onShowArchivedChange,
  showColors,
  onShowColorsChange,
  activeFilterCount,
  onClearFilters,
  onTeamChange,
  onOpenColumnSettings,
}: ContentTrackerHeaderProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(() => {
    try {
      const saved = localStorage.getItem('content-tracker-filters-collapsed');
      return saved !== 'true';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('content-tracker-filters-collapsed', String(!isDrawerOpen));
    } catch {
      // Ignore localStorage errors
    }
  }, [isDrawerOpen]);

  const suffix = isFiltered
    ? `(${filteredCount}/${totalCampaigns} Campaigns)`
    : `(${totalCampaigns} Campaign${totalCampaigns !== 1 ? 's' : ''})`;

  const actions = (
    <>
      {/* Spacer */}
      <div className="flex-1" />

      {/* Hide Colors Toggle — icon only, blue when colors are hidden */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => onShowColorsChange(!showColors)}
            className={`h-8 w-8 rounded-lg outline-none inline-flex items-center justify-center shrink-0 transition-colors border ${
              !showColors
                ? 'bg-[#0014cc] border-[#0014cc] text-white hover:bg-[#0014cc]/90'
                : 'bg-white text-slate-500 border-slate-300 hover:border-slate-400 hover:text-slate-700'
            }`}
          >
            <div className="relative">
              <Palette className="w-3.5 h-3.5" />
              {!showColors && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-[18px] h-[2px] bg-current rotate-45 rounded-full" />
                </div>
              )}
            </div>
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {showColors ? 'Hide SLA row colors' : 'Show SLA row colors'}
        </TooltipContent>
      </Tooltip>

      {/* Archive Toggle */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => onShowArchivedChange(!showArchived)}
            className={`h-8 px-2.5 rounded-lg text-sm font-medium outline-none flex items-center gap-2 whitespace-nowrap shrink-0 transition-colors border ${
              showArchived
                ? 'bg-[#0014cc] text-white border-[#0014cc] hover:bg-[#0014cc]/90'
                : 'bg-white text-slate-500 border-slate-300 hover:border-slate-400 hover:text-slate-700'
            }`}
          >
            <Archive className={`w-3.5 h-3.5 ${showArchived ? 'text-white' : 'text-slate-500'}`} />
            <span>Archived</span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {showArchived ? 'Hide archived campaigns' : 'Show archived campaigns'}
        </TooltipContent>
      </Tooltip>

      {/* Customize Columns */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onOpenColumnSettings}
            className="h-8 px-2.5 border border-slate-300 rounded-lg text-sm font-medium bg-white outline-none flex items-center gap-2 whitespace-nowrap shrink-0 transition-colors text-slate-500 hover:border-slate-400 hover:text-slate-700"
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            <span>Columns</span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Customize visible columns</TooltipContent>
      </Tooltip>

      {/* Filters Toggle */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => setIsDrawerOpen(!isDrawerOpen)}
            className={`h-8 px-2.5 rounded-lg text-sm font-medium outline-none flex items-center gap-2 whitespace-nowrap shrink-0 transition-colors border ${
              isDrawerOpen
                ? 'bg-[#0014cc] text-white border-[#0014cc] hover:bg-[#0014cc]/90'
                : 'bg-white text-slate-500 border-slate-300 hover:border-slate-400 hover:text-slate-700'
            }`}
          >
            <Filter className={`w-3.5 h-3.5 ${isDrawerOpen ? 'text-white' : 'text-slate-500'}`} />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span
                className={`text-xs font-bold px-1.5 py-0.5 rounded-md min-w-[1.25rem] text-center ${isDrawerOpen ? 'bg-white text-[#0014cc]' : 'bg-[#0014cc] text-white'}`}
              >
                {activeFilterCount}
              </span>
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {isDrawerOpen ? 'Close filters drawer' : 'Open filters drawer'}
        </TooltipContent>
      </Tooltip>
    </>
  );

  return (
    <PageHeader>
      <Breadcrumb actions={actions} suffix={suffix} />
      <FilterDrawer
        isOpen={isDrawerOpen}
        filters={filters}
        onFilterChange={onFilterChange}
        availableOptions={availableOptions}
        activeFilterCount={activeFilterCount}
        onClearFilters={onClearFilters}
        onTeamChange={onTeamChange}
      />
    </PageHeader>
  );
}
